import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useMe } from '../../hooks/useAuth';
import { formatCop } from '../../lib/format';
import { getKpis } from './dashboard.api';
import { STATE_COLORS, STATE_LABELS, STATE_ORDER } from '../os/state-machine';

export function DashboardPage() {
  const me = useMe();
  const kpis = useQuery({ queryKey: ['kpis'], queryFn: getKpis, refetchInterval: 60_000 });

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Hola, {me.data?.fullName?.split(' ')[0]}</h1>
        <span style={{ color: 'var(--color-muted)', fontSize: '0.9rem' }}>
          {new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
        <a
          className="btn"
          style={{ marginLeft: 'auto' }}
          href={`/api/reports/daily-handover?date=${new Date().toISOString().slice(0, 10)}`}
          target="_blank"
          rel="noreferrer"
          title="Resumen del día (PDF para entrega de turno)"
        >
          📋 Resumen del día (PDF)
        </a>
      </div>

      {kpis.isLoading && <p>Cargando métricas...</p>}
      {kpis.data && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}
          >
            <KpiCard
              label="Ventas hoy"
              value={formatCop(kpis.data.today.salesTotal)}
              hint={`${kpis.data.today.invoiceCount} factura(s)`}
            />
            <KpiCard
              label="Ventas del mes"
              value={formatCop(kpis.data.month.salesTotal)}
              hint={`${kpis.data.month.invoiceCount} factura(s)`}
            />
            <KpiCard
              label="Stock bajo mínimo"
              value={String(kpis.data.lowStockCount)}
              hint="ítems requieren reabastecimiento"
              accent={kpis.data.lowStockCount > 0 ? 'var(--color-danger)' : undefined}
              link={{ to: '/warehouses', label: 'Ver bodegas →' }}
            />
            <KpiCard
              label="DIAN pendiente"
              value={String(kpis.data.dianPending)}
              hint="facturas esperando CUFE"
              accent={kpis.data.dianPending > 0 ? 'var(--color-accent)' : undefined}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '1rem' }}>
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Pipeline de Servicio</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {STATE_ORDER.filter((s) => s !== 'DELIVERED').map((s) => {
                  const count = kpis.data!.serviceOrders[s] ?? 0;
                  return (
                    <Link
                      key={s}
                      to="/orders"
                      search={{ state: s }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.6rem',
                        padding: '0.5rem 0.75rem',
                        background: 'var(--color-bg)',
                        borderRadius: 'var(--radius)',
                        color: 'var(--color-text)',
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: STATE_COLORS[s],
                        }}
                      />
                      <span style={{ flex: 1 }}>{STATE_LABELS[s]}</span>
                      <strong>{count}</strong>
                    </Link>
                  );
                })}
              </div>
              <Link to="/orders" className="btn" style={{ marginTop: '0.75rem', display: 'inline-block' }}>
                Ver todas las OS →
              </Link>
            </div>

            <div className="card">
              <h3 style={{ marginTop: 0 }}>Últimas facturas</h3>
              {kpis.data.recentInvoices.length === 0 ? (
                <p style={{ color: 'var(--color-muted)' }}>Sin facturas todavía.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {kpis.data.recentInvoices.map((inv) => (
                    <li
                      key={inv.id}
                      style={{
                        padding: '0.5rem 0',
                        borderBottom: '1px solid var(--color-border)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '0.5rem',
                      }}
                    >
                      <div>
                        <code>{inv.number}</code>
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                          {inv.customer}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <strong>{formatCop(inv.total)}</strong>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                          {inv.status.replace('DIAN_', '')}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function KpiCard({
  label,
  value,
  hint,
  accent,
  link,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
  link?: { to: string; label: string };
}): ReactNode {
  return (
    <div className="card" style={{ borderLeft: accent ? `4px solid ${accent}` : undefined }}>
      <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', color: 'var(--color-muted)' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, marginTop: '0.25rem' }}>{value}</div>
      {hint && <div style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>{hint}</div>}
      {link && (
        <Link to={link.to} style={{ fontSize: '0.85rem', color: 'var(--color-accent)' }}>
          {link.label}
        </Link>
      )}
    </div>
  );
}
